import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const PractitionerProfile = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    bio: '',
    social_instagram: '',
    social_facebook: '',
    social_website: '',
    social_tiktok: ''
  });
  const [photoPath, setPhotoPath] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/profile`, { headers });
      setFormData({
        bio: response.data.bio || '',
        social_instagram: response.data.social_instagram || '',
        social_facebook: response.data.social_facebook || '',
        social_website: response.data.social_website || '',
        social_tiktok: response.data.social_tiktok || ''
      });
      setPhotoPath(response.data.photo_path || '');
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/practitioners/${userId}/profile`, formData, { headers });
      setSuccess('Profile saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const photoForm = new FormData();
    photoForm.append('photo', file);
    try {
      const response = await axios.post(`${apiUrl}/practitioners/${userId}/profile/photo`, photoForm, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setPhotoPath(response.data.photo_path);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <div className="page-container">Loading profile...</div>;

  return (
    <div className="page-container" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1>My Profile</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>Back to Dashboard</button>
      </div>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="card" style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)', padding: 12 }}>{success}</div>}

      <div className="card">
        <div className="card-header">Photo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {photoPath ? (
            <img
              src={`${apiUrl}/uploads/profiles/${photoPath}`}
              alt="Profile"
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>
              No photo
            </div>
          )}
          <label className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }}>
            {uploading ? 'Uploading...' : 'Upload Photo'}
            <input type="file" accept="image/*" onChange={handlePhotoChange} disabled={uploading} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-header">About Me &amp; Social Links</div>
        <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: -8, marginBottom: 14 }}>
          Shown to clients alongside your services and bookings.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Bio</label>
            <textarea
              rows="4"
              placeholder="Tell clients a little about yourself..."
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Instagram</label>
            <input
              placeholder="https://instagram.com/yourhandle"
              value={formData.social_instagram}
              onChange={(e) => setFormData({ ...formData, social_instagram: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Facebook</label>
            <input
              placeholder="https://facebook.com/yourpage"
              value={formData.social_facebook}
              onChange={(e) => setFormData({ ...formData, social_facebook: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Website</label>
            <input
              placeholder="https://yoursite.com"
              value={formData.social_website}
              onChange={(e) => setFormData({ ...formData, social_website: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>TikTok</label>
            <input
              placeholder="https://tiktok.com/@yourhandle"
              value={formData.social_tiktok}
              onChange={(e) => setFormData({ ...formData, social_tiktok: e.target.value })}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PractitionerProfile;
